import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  InlineSelect,
  courseLabel,
  formatDateTime,
  liveSessionStatusOptions,
} from '@/components/admin/AcademicTableHelpers';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { createLiveSession, deleteLiveSession, endLiveSession, getCourses, getLiveSessions, getMyCourses, getMyLiveSessions, getZoomStatus, provisionZoomMeeting, startLiveSession, updateLiveSession } from '@/lib/api';
import { dateTimeLocalToIsoValue, toDateTimeLocalInputValue } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarClock, ExternalLink, Info, Pencil, Plus, Radio, Square, Trash2 } from 'lucide-react';

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

/**
 * Why "Create Zoom" is unavailable, in terms the reader can act on.
 *
 * The integration is complete in code; it needs credentials. Naming the exact
 * variables turns a dead 503 into something an administrator can fix.
 */
function ZoomSetupNotice({ status }) {
  if (!status || status.in_portal_streaming_ready) return null;
  const missing = [...(status.missing_api || []), ...(status.missing_sdk || [])];
  return (
    <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <div className="text-sm text-amber-900">
        <p className="font-semibold">Streaming inside the portal is not switched on yet.</p>
        <p className="mt-1 text-amber-900/80">
          You can still run classes now by pasting a Google Meet, Teams or Zoom link into a session —
          students join it from the portal and their attendance is recorded either way. To host the
          video inside the portal instead, these environment variables need values on the backend:
        </p>
        <p className="mt-2 font-mono text-xs text-amber-900">{missing.join(', ')}</p>
      </div>
    </div>
  );
}

export default function ManageLiveSessions({ instructorMode = false }) {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savingInline, setSavingInline] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [zoomStatus, setZoomStatus] = useState(null);
  const [form, setForm] = useState({
    title: '',
    course_id: '',
    scheduled_time: '',
    duration_minutes: 60,
    meeting_link: '',
    description: '',
    status: 'upcoming',
    recording_url: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionRes, courseRes] = await Promise.all([
        instructorMode ? getMyLiveSessions() : getLiveSessions({ course_id: filterParam(courseFilter), status: filterParam(statusFilter) }),
        instructorMode ? getMyCourses() : getCourses(),
      ]);
      getZoomStatus().then(({ data }) => setZoomStatus(data)).catch(() => setZoomStatus(null));
      const nextSessions = sessionRes.data || [];
      setSessions(nextSessions);
      setCourses(courseRes.data || []);
      setSelectedIds((prev) => {
        const visible = new Set(nextSessions.map((session) => session.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not load live sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, courseFilter, statusFilter]);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => { map[course.id] = course; });
    return map;
  }, [courses]);

  const filteredSessions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sessions;
    return sessions.filter((session) => {
      const course = courseMap[session.course_id];
      return (
        (session.title || '').toLowerCase().includes(query)
        || courseLabel(course).toLowerCase().includes(query)
        || (session.meeting_link || '').toLowerCase().includes(query)
      );
    });
  }, [courseMap, search, sessions]);

  const selectedSessions = useMemo(
    () => sessions.filter((session) => selectedIds.has(session.id)),
    [selectedIds, sessions],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', course_id: '', scheduled_time: '', duration_minutes: 60, meeting_link: '', description: '', status: 'upcoming', recording_url: '' });
    setShowDialog(true);
  };

  const openEdit = (session) => {
    setEditing(session);
    setForm({
      title: session.title || '',
      course_id: session.course_id || '',
      scheduled_time: toDateTimeLocalInputValue(session.scheduled_time),
      duration_minutes: session.duration_minutes || 60,
      meeting_link: session.meeting_link || '',
      description: session.description || '',
      status: session.status || 'upcoming',
      recording_url: session.recording_url || '',
    });
    setShowDialog(true);
  };

  const handleLifecycle = async (session, action) => {
    try {
      setSavingInline(session.id);
      const { data } = await (action === 'start' ? startLiveSession(session.id) : endLiveSession(session.id));
      setSessions((prev) => prev.map((item) => (item.id === session.id ? data : item)));
      toast.success(action === 'start' ? 'Session is live — students can join now' : 'Session ended');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update this session');
      await loadData();
    } finally {
      setSavingInline('');
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.course_id) {
      toast.error('Title and course are required');
      return;
    }
    try {
      const payload = {
        ...form,
        scheduled_time: dateTimeLocalToIsoValue(form.scheduled_time),
      };
      if (editing) {
        await updateLiveSession(editing.id, payload);
        toast.success('Live session updated');
      } else {
        await createLiveSession(payload);
        toast.success('Live session scheduled');
      }
      setShowDialog(false);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save live session');
    }
  };

  const handleInlineStatus = async (session, status) => {
    try {
      setSavingInline(session.id);
      const { data } = await updateLiveSession(session.id, { status });
      setSessions((prev) => prev.map((item) => (item.id === session.id ? data : item)));
      toast.success('Live session status updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update status');
      await loadData();
    } finally {
      setSavingInline('');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, sessions: items });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.sessions.map((session) => session.id);
      await Promise.all(ids.map((id) => deleteLiveSession(id)));
      toast.success(`${ids.length} live session${ids.length === 1 ? '' : 's'} deleted`);
      setPendingDelete(null);
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete live sessions');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      id: 'title',
      header: 'Title',
      sortable: true,
      sortAccessor: (session) => session.title || '',
      cellClassName: 'min-w-[240px]',
      cell: (session) => (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#062B1A]/5 flex items-center justify-center">
            <CalendarClock className="w-4 h-4 text-[#062B1A]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{session.title || 'Untitled session'}</p>
            <p className="text-xs text-[#062B1A]/45">{session.description ? 'Description added' : 'No description'}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'course',
      header: 'Course',
      sortable: true,
      sortAccessor: (session) => courseLabel(courseMap[session.course_id]),
      cellClassName: 'min-w-[240px] text-sm text-[#062B1A]/70',
      cell: (session) => courseLabel(courseMap[session.course_id]),
    },
    {
      id: 'scheduled_time',
      header: 'Scheduled time',
      sortable: true,
      cellClassName: 'min-w-[190px] text-sm text-[#062B1A]/70',
      cell: (session) => formatDateTime(session.scheduled_time),
    },
    {
      id: 'duration_minutes',
      header: 'Duration (min)',
      sortable: true,
      sortAccessor: (session) => Number(session.duration_minutes || 0),
      cellClassName: 'min-w-[130px] text-sm text-[#062B1A]/70',
      cell: (session) => session.duration_minutes || 0,
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[170px]',
      cell: (session) => (
        <InlineSelect
          value={session.status || 'upcoming'}
          disabled={savingInline === session.id}
          options={liveSessionStatusOptions}
          onChange={(value) => handleInlineStatus(session, value)}
        />
      ),
    },
    {
      id: 'meeting_link',
      header: 'Meeting link',
      sortable: true,
      cellClassName: 'min-w-[210px]',
      cell: (session) => session.meeting_link ? (
        <a
          href={session.meeting_link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-[#062B1A] hover:text-[#D4AF37]"
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open link
        </a>
      ) : (
        <span className="text-sm text-[#062B1A]/35">-</span>
      ),
    },
  ];

  const rowActions = (session) => (
    <div className="flex justify-end gap-2">
      {instructorMode && session.status !== 'completed' && session.status !== 'cancelled' && (
        session.status === 'live' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50"
            disabled={savingInline === session.id}
            onClick={() => handleLifecycle(session, 'end')}
          >
            <Square className="w-3.5 h-3.5 mr-1.5" />End
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="bg-green-700 text-white hover:bg-green-800"
            disabled={savingInline === session.id}
            onClick={() => handleLifecycle(session, 'start')}
          >
            <Radio className="w-3.5 h-3.5 mr-1.5" />Go live
          </Button>
        )
      )}
      {instructorMode && (
        session.meeting_provider === 'zoom' || session.meeting_link ? (
          <Button type="button" size="sm" className="bg-[#062B1A] text-white" onClick={() => navigate(`/instructor/live-sessions/${session.id}`)}>Open room</Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
            disabled={zoomStatus ? !zoomStatus.api_configured : false}
            title={zoomStatus && !zoomStatus.api_configured ? 'Zoom credentials are not configured yet' : undefined}
            onClick={async () => {
              try {
                await provisionZoomMeeting(session.id);
                toast.success('Zoom meeting created');
                await loadData();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Could not create Zoom meeting');
              }
            }}
          >
            Create Zoom
          </Button>
        )
      )}
      <Button type="button" variant="outline" size="sm" className="border-[#062B1A]/15 text-[#062B1A]" onClick={() => openEdit(session)}>
        <Pencil className="w-4 h-4 mr-2" />
        Edit
      </Button>
      <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => confirmDelete([session])}>
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </Button>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <Button
      type="button"
      size="sm"
      disabled={selectedCount === 0}
      className="bg-red-700 text-white hover:bg-red-800"
      onClick={() => confirmDelete(selectedSessions, 'bulk')}
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Bulk Delete
    </Button>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Manage Live Sessions</h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">{sessions.length} live session{sessions.length === 1 ? '' : 's'} in the current view</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Schedule Session
        </Button>
      </div>

      {instructorMode && <ZoomSetupNotice status={zoomStatus} />}

      <DataTable
        title="Live Session Management"
        description="Filter by course or status, edit records in full, and update session state inline."
        data={filteredSessions}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search sessions, courses, or meeting links..."
        filters={[
          { id: 'course', value: courseFilter, onChange: setCourseFilter, options: [{ value: 'all', label: 'All courses' }, ...courses.map((course) => ({ value: course.id, label: courseLabel(course) }))] },
          { id: 'status', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All statuses' }, ...liveSessionStatusOptions] },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={openEdit}
        emptyMessage="No live sessions match the current filters."
        pageSize={8}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>{editing ? 'Edit' : 'Schedule'} Live Session</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course *</label>
              <Select value={form.course_id} onValueChange={(value) => setForm((prev) => ({ ...prev, course_id: value }))}>
                <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{courses.map((course) => <SelectItem key={course.id} value={course.id}>{courseLabel(course)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Scheduled Time</label>
                <Input type="datetime-local" value={form.scheduled_time} onChange={(event) => setForm((prev) => ({ ...prev, scheduled_time: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Duration (min)</label>
                <Input type="number" value={form.duration_minutes} onChange={(event) => setForm((prev) => ({ ...prev, duration_minutes: parseInt(event.target.value, 10) || 0 }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Meeting Link</label>
              <Input value={form.meeting_link} onChange={(event) => setForm((prev) => ({ ...prev, meeting_link: event.target.value }))} placeholder="https://..." className="border-[rgba(6,43,26,0.12)]" />
              <p className="text-xs text-[#062B1A]/40 mt-1">Zoom, Google Meet or Teams — students join it from inside the portal.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Recording Link</label>
              <Input value={form.recording_url} onChange={(event) => setForm((prev) => ({ ...prev, recording_url: event.target.value }))} placeholder="https://..." className="border-[rgba(6,43,26,0.12)]" />
              <p className="text-xs text-[#062B1A]/40 mt-1">Shown to students as a replay once the class is over.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Description</label>
              <Textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Status</label>
              <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                <SelectContent>{liveSessionStatusOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>{editing ? 'Update' : 'Schedule'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.sessions?.length || 0} live session${pendingDelete?.sessions?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected live session records."
        count={pendingDelete?.sessions?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
